import React, { useEffect, useState, useRef, createElement, Component } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { PLAN_PRICES_USD, formatUsd } from '../data/pricingPlans';
import { AlertTriangle, Shield, MessageSquare, TrendingDown, Share2, Lock, Sparkles, Download, RefreshCw, Instagram, Facebook, Twitter, MessageCircle, Activity, ScanLine } from 'lucide-react';
import { useScan } from '../hooks/useScans';
import { motion } from 'framer-motion';
import { getMessageCount } from '../utils/scanReport';
import { trackEvent, trackEventOnce } from '../utils/visitorTracking';
export function PublicResultsPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const scanId = searchParams.get('id');
  const {
    scan,
    loading,
    error
  } = useScan(scanId || undefined);
  const [downloading, setDownloading] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  // Only when the completed result has genuinely loaded and rendered — never
  // on a bare mount while the scan is still loading or failed. Keyed on the
  // scan id so Strict Mode or a refresh cannot double-count the view.
  useEffect(() => {
    if (!scan || scan.status !== 'completed') return;
    void trackEventOnce(`result_viewed:${scan.id}`, 'result_viewed', {
      scan_id: scan.id,
      score: scan.score ?? null,
      risk_level: scan.risk_level ?? null,
      source: 'public_results_page'
    });
  }, [scan?.id, scan?.status]);
  const handleDownloadImage = async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      const {
        toPng
      } = await import('html-to-image');
      const dataUrl = await toPng(cardRef.current, {
        quality: 1,
        pixelRatio: 2,
        backgroundColor: '#050505'
      });
      const link = document.createElement('a');
      link.download = `toxicity-result-${scanId}.png`;
      link.href = dataUrl;
      link.click();
      setDownloading(false);
    } catch (err) {
      console.error('Failed to generate image:', err);
      setDownloading(false);
    }
  };
  const handleShareToSocial = async (platform: string) => {
    const shareUrl = window.location.href;
    const text = `I just analyzed my chat on ToxicDetector! 🚩 My toxicity score: ${scan?.score || 0}%`;
    let url = '';
    try {
      switch (platform) {
        case 'twitter':
          url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`;
          window.open(url, '_blank', 'width=600,height=400');
          break;
        case 'facebook':
          url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
          window.open(url, '_blank', 'width=600,height=400');
          break;
        case 'whatsapp':
          url = `https://wa.me/?text=${encodeURIComponent(text + ' ' + shareUrl)}`;
          window.open(url, '_blank', 'width=600,height=400');
          break;
        case 'instagram':
        case 'tiktok':
          const fullText = `${text}\n\n${shareUrl}`;
          if (navigator.clipboard && navigator.clipboard.writeText) {
            await navigator.clipboard.writeText(fullText);
            const platformName = platform === 'instagram' ? 'Instagram' : 'TikTok';
            alert(`✓ Link copied! Open ${platformName} and paste it in your story or post.`);
          }
          break;
      }
      // Fires only after the share window opened or the link was copied.
      void trackEvent('result_shared', {
        scan_id: scanId,
        platform,
        method: ['instagram', 'tiktok'].includes(platform) ? 'copy_link' : 'web_intent',
        score: scan?.score ?? null,
        source: 'public_results_page'
      });
      setShowShareMenu(false);
    } catch (err) {
      console.error('Share error:', err);
      alert('Failed to copy link. Please copy manually: ' + shareUrl);
    }
  };
  if (!scanId) {
    return <div className="flex flex-col w-full min-h-screen bg-[#050505] text-white" data-id="element-9730" data-mp="25sw7o59pwkls">
        <header className="bg-white/5 backdrop-blur-md border-b border-white/10" data-id="element-9731" data-mp="20vctt6gxm9ud">
          <div className="max-w-4xl mx-auto px-5 py-4 flex items-center gap-2" data-id="element-9732" data-mp="2fjh69spe5p4p">
            <Activity className="text-red-500" data-id="element-9733" data-mp="2alfnz9zshtua" />
            <h1 className="text-xl font-black tracking-tight" data-id="element-9734" data-mp="2ansdc87vn6hd">ToxicDetector</h1>
          </div>
        </header>
        <div className="flex-1 flex items-center justify-center px-5" data-id="element-9735" data-mp="1mxy0vrrgzjxn">
          <div className="text-center" data-id="element-9736" data-mp="1hyuw88fejfq7">
            <p className="text-white/60 mb-6 font-mono" data-id="element-9737" data-mp="1vwji9jl7gswu">No scan ID provided</p>
            <Link to="/" className="inline-block bg-white text-black px-8 py-3 rounded-xl font-bold hover:bg-gray-200 transition-all" data-id="element-9738" data-mp="1vugi46qc9jee">
              Go to Home
            </Link>
          </div>
        </div>
      </div>;
  }
  if (loading) {
    return <div className="flex flex-col w-full min-h-screen bg-[#050505] text-white" data-id="element-9739" data-mp="33cpjjcytdctr">
        <header className="bg-white/5 backdrop-blur-md border-b border-white/10" data-id="element-9740" data-mp="3askrc06hd6cf">
          <div className="max-w-4xl mx-auto px-5 py-4 flex items-center gap-2" data-id="element-9741" data-mp="3fujvhca1o7af">
            <Activity className="text-red-500" data-id="element-9742" data-mp="30wriaqryzuze" />
            <h1 className="text-xl font-black tracking-tight" data-id="element-9743" data-mp="30y2k4l1uusnv">ToxicDetector</h1>
          </div>
        </header>
        <div className="flex-1 flex items-center justify-center px-5" data-id="element-9744" data-mp="3tkdidp1zohhi">
          <div className="text-center" data-id="element-9745" data-mp="3tl5dyzvl6f7s">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-red-500 border-t-transparent mx-auto mb-6" data-id="element-9746" data-mp="2dsv8gf21tpr"></div>
            <p className="text-white/60 font-mono uppercase tracking-widest" data-id="element-9747" data-mp="3jtiu6f56n7tf">
              Analyzing Data...
            </p>
          </div>
        </div>
      </div>;
  }
  if (error || !scan) {
    return <div className="flex flex-col w-full min-h-screen bg-[#050505] text-white" data-id="element-9748" data-mp="gcai6bx4z26m">
        <header className="bg-white/5 backdrop-blur-md border-b border-white/10" data-id="element-9749" data-mp="l5lp0d1y7iyj">
          <div className="max-w-4xl mx-auto px-5 py-4 flex items-center gap-2" data-id="element-9750" data-mp="v0snjqu6fvjy">
            <Activity className="text-red-500" data-id="element-9751" data-mp="q1phq46dhlax" />
            <h1 className="text-xl font-black tracking-tight" data-id="element-9752" data-mp="q1z99w72g0iq">ToxicDetector</h1>
          </div>
        </header>
        <div className="flex-1 flex items-center justify-center px-5" data-id="element-9753" data-mp="ly1a5lhqqppo">
          <div className="text-center" data-id="element-9754" data-mp="gzq1he36ml91">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" data-id="element-9755" data-mp="h2vs4xrglv6v" />
            <p className="text-red-400 mb-6 font-mono" data-id="element-9756" data-mp="c39a8b0orqrq">
              {error || 'Results not found'}
            </p>
            <Link to="/" className="inline-block bg-white text-black px-8 py-3 rounded-xl font-bold hover:bg-gray-200 transition-all" data-id="element-9757" data-mp="78vdo5aafdlh">
              Go to Home
            </Link>
          </div>
        </div>
      </div>;
  }
  const score = scan.score || 0;
  const riskLevel = scan.risk_level || 'low';
  const patterns = scan.patterns || [];
  const isPaid = scan.is_unlocked || false;
  const getWittyVerdict = (score: number): string => {
    if (score >= 85) return "Girl, RUN. That's manipulation 101. 🏃‍♀️";
    if (score >= 70) return 'Major red flags detected. Trust your gut. 🚩';
    if (score >= 50) return "Proceed with caution. Something's off here. ⚠️";
    if (score >= 30) return 'Not terrible, but keep your eyes open. 👀';
    return 'Looking pretty healthy! Keep it that way. ✨';
  };
  const getThemeColors = (level: string) => {
    if (level === 'high') return {
      text: 'text-red-500',
      bg: 'bg-red-500',
      glow: 'bg-red-500/20',
      border: 'border-red-500/30'
    };
    if (level === 'medium') return {
      text: 'text-amber-500',
      bg: 'bg-amber-500',
      glow: 'bg-amber-500/20',
      border: 'border-amber-500/30'
    };
    return {
      text: 'text-emerald-500',
      bg: 'bg-emerald-500',
      glow: 'bg-emerald-500/20',
      border: 'border-emerald-500/30'
    };
  };
  const theme = getThemeColors(riskLevel);
  return <div className="flex flex-col w-full min-h-screen bg-[#050505] text-white relative overflow-hidden" data-id="element-9758" data-mp="3s1bxz72zz8jw">
      {/* Background Glow */}
      <div className={`absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full ${theme.glow} blur-[120px] mix-blend-screen opacity-40 pointer-events-none`} data-id="element-9759" data-mp="3n4v8o9k2ao58" />

      {/* Header */}
      <header className="bg-white/5 backdrop-blur-xl border-b border-white/10 sticky top-0 z-50" data-id="element-9760" data-mp="xx1vj895wqi">
        <div className="max-w-4xl mx-auto px-5 py-4" data-id="element-9761" data-mp="etijv6g2jg9f">
          <div className="flex items-center justify-between" data-id="element-9762" data-mp="9vh34s647t14">
            <div className="flex items-center gap-2" data-id="element-9763" data-mp="olev7j7612vl">
              <Activity className={theme.text} data-id="element-9764" data-mp="okvdo3lfggi3" />
              <h1 className="text-xl font-black tracking-tight" data-id="element-9765" data-mp="jmlhpqrvpbvf">
                ToxicDetector
              </h1>
            </div>
            <Link to="/signup" className="text-white/60 text-sm font-bold hover:text-white transition-colors" data-id="element-9766" data-mp="xn4o6tgp49vh">
              Try it yourself
            </Link>
          </div>
        </div>
      </header>

      <div className="flex-1 w-full max-w-2xl mx-auto px-4 py-8 relative z-10" data-id="element-9767" data-mp="1c6p70ygapyub">
        {!isPaid ? <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} data-id="element-9768" data-mp="1h5iot32m8c0a">
            {/* Teaser Card */}
            <div className="bg-white/5 backdrop-blur-xl rounded-[32px] p-8 md:p-12 border border-white/10 shadow-2xl mb-8 relative overflow-hidden text-center" data-id="element-9769" data-mp="tghd96yhkkqs">
              <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-white/20 to-transparent" data-id="element-9770" data-mp="kj6yhbmgmdh4" />

              <div className="w-20 h-20 mx-auto bg-white/10 rounded-2xl flex items-center justify-center mb-8 border border-white/10" data-id="element-9771" data-mp="oguuyj3sweb4">
                <Lock className="w-10 h-10 text-white/50" data-id="element-9772" data-mp="fisovp57mvgz" />
              </div>

              <h2 className="text-3xl md:text-4xl font-black text-white tracking-tight mb-4" data-id="element-9773" data-mp="1hswawav3md4j">
                Results Locked
              </h2>
              <p className="text-white/60 font-mono text-sm mb-10 max-w-sm mx-auto" data-id="element-9774" data-mp="1lv179hcxlnuv">
                {getMessageCount(scan) ? `We've analyzed ${getMessageCount(scan)} messages and found potential patterns of manipulation.` : "We've analyzed this conversation and found potential patterns of manipulation."}
              </p>

              {/* Blurred Preview */}
              <div className="relative mb-10" data-id="element-9775" data-mp="25hpcamuhpd6j">
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent z-10" data-id="element-9776" data-mp="1qtch3s387yd3" />
                <div className="filter blur-md opacity-50 select-none" data-id="element-9777" data-mp="1vrnnfsubbqqk">
                  <div className="text-8xl font-black text-white mb-4" data-id="element-9778" data-mp="2ji05vxn6p5ve">84</div>
                  <div className="h-24 bg-white/10 rounded-xl mx-auto max-w-xs mb-4" data-id="element-9779" data-mp="2jfx5qksbhwcy" />
                  <div className="h-24 bg-white/10 rounded-xl mx-auto max-w-xs" data-id="element-9780" data-mp="2oa1bj0pujycg" />
                </div>
              </div>

              <Link to="/payment" className="block w-full bg-white text-black text-lg font-black py-5 rounded-2xl hover:bg-gray-200 transition-all shadow-lg hover:scale-[1.02] active:scale-[0.98]" data-id="element-9781" data-mp="crenp9j0rna4">
                Unlock Full Report — {formatUsd(PLAN_PRICES_USD.single_scan)}
              </Link>
              <p className="text-white/40 text-xs font-mono mt-4 uppercase tracking-widest" data-id="element-9782" data-mp="3q0janoe93dik">
                One-time payment • Instant access
              </p>
            </div>
          </motion.div> : <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} data-id="element-9783" data-mp="2t79gyg47jbt">
            {/* Main Result Card - Shareable */}
            <div ref={cardRef} className="bg-[#050505] rounded-[32px] p-8 md:p-12 border border-white/10 shadow-2xl mb-8 relative overflow-hidden" data-id="element-9784" data-mp="6yhmbg2mwhko">
              <div className={`absolute top-0 left-1/2 -translate-x-1/2 w-[400px] h-[400px] rounded-full ${theme.glow} blur-[80px] mix-blend-screen opacity-50 pointer-events-none`} data-id="element-9785" data-mp="fyw96jv8y4qa" />

              <div className="relative z-10" data-id="element-9786" data-mp="3kdnr8k3hj86f">
                {/* Header */}
                <div className="flex items-center justify-between mb-12" data-id="element-9787" data-mp="3oephibffc9w9">
                  <div className="flex items-center gap-3" data-id="element-9788" data-mp="3oezbea3lb178">
                    <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center" data-id="element-9789" data-mp="3tcrbhree1tmi">
                      <ScanLine className="w-5 h-5 text-white" data-id="element-9790" data-mp="20poy6j80393" />
                    </div>
                    <span className="font-black text-white tracking-tight uppercase" data-id="element-9791" data-mp="30od8syccu94m">
                      ToxicDetector
                    </span>
                  </div>
                  <span className="text-xs font-mono text-white/50 uppercase tracking-widest" data-id="element-9792" data-mp="2y4ysnfb3jc3s">
                    {new Date().toLocaleDateString()}
                  </span>
                </div>

                {/* Verdict */}
                <div className="text-center mb-12" data-id="element-9793" data-mp="2o4y5tje0qduu">
                  <h2 className="text-4xl md:text-5xl font-black text-white tracking-tight leading-tight" data-id="element-9794" data-mp="2k57ijpnpafv2">
                    {getWittyVerdict(score)}
                  </h2>
                </div>

                {/* Score */}
                <div className="flex flex-col items-center justify-center mb-12" data-id="element-9795" data-mp="2ae35ivhl552v">
                  <div className="relative w-48 h-48 flex items-center justify-center mb-6" data-id="element-9796" data-mp="25fi4wuyf8jdw">
                    <svg className="w-full h-full transform -rotate-90" viewBox="0 0 200 200" data-id="element-9797" data-mp="20hglg8x32i1w">
                      <circle cx="100" cy="100" r="88" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="12" strokeLinecap="round" data-id="element-9798" data-mp="1whfuxpwpmgeh" />
                      <circle cx="100" cy="100" r="88" fill="none" stroke="currentColor" className={theme.text} strokeWidth="12" strokeLinecap="round" strokeDasharray={2 * Math.PI * 88} strokeDashoffset={2 * Math.PI * 88 - score / 100 * (2 * Math.PI * 88)} data-id="element-9799" data-mp="3hsydi6lytluu" />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-9800" data-mp="x2qywmze53ph">
                      <span className="text-6xl font-black text-white tracking-tighter" data-id="element-9801" data-mp="121aiwhufc5f7">
                        {score}
                      </span>
                    </div>
                  </div>
                  <div className={`inline-flex items-center gap-2 px-6 py-2 rounded-full bg-white/5 border ${theme.border} backdrop-blur-md`} data-id="element-9802" data-mp="176o66q9418rx">
                    <span className={`text-sm font-black ${theme.text} uppercase tracking-widest`} data-id="element-9803" data-mp="1kznfubm7o8rk">
                      {riskLevel} Risk
                    </span>
                  </div>
                </div>

                {/* Patterns */}
                {patterns.length > 0 && <div className="space-y-4 mb-12" data-id="element-9804" data-mp="23wyhg2vbfea6">
                    {patterns.slice(0, 3).map((pattern: any, index) => <div key={index} className="bg-white/5 backdrop-blur-md rounded-2xl p-5 border border-white/10 flex items-start gap-4" data-id="element-9805" data-mp="1zwehkrjcxwkc" data-mpi={index}>
                        <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0" data-id="element-9806" data-mp="5ivel26k5lrn">
                          {getPatternIcon(pattern.type)}
                        </div>
                        <div data-id="element-9807" data-mp="29mzs80ywcdrj">
                          <div className="flex items-center gap-3 mb-1" data-id="element-9808" data-mp="2ek9nvycvplnt">
                            <h4 className="font-bold text-white text-lg" data-id="element-9809" data-mp="2emcofcbnqx6s">
                              {pattern.type}
                            </h4>
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-widest border ${getPatternColorClass(pattern.severity)}`} data-id="element-9810" data-mp="2njn1n381cwec">
                              {pattern.severity}
                            </span>
                          </div>
                          <p className="text-white/60 text-sm leading-relaxed" data-id="element-9811" data-mp="32bnfg237hd7q">
                            {pattern.description}
                          </p>
                        </div>
                      </div>)}
                  </div>}

                {/* Footer Stats */}
                <div className="flex items-center justify-between pt-6 border-t border-white/10" data-id="element-9812" data-mp="vlbqrbjaojww">
                  <div className="flex gap-8" data-id="element-9813" data-mp="qm7kasnr90a9">
                    <div data-id="element-9814" data-mp="qn0i344qkfg9">
                      <div className="text-2xl font-black text-white" data-id="element-9815" data-mp="qmh8cap8oxdb">
                        {getMessageCount(scan) ?? '—'}
                      </div>
                      <div className="text-[10px] text-white/40 font-mono uppercase tracking-widest" data-id="element-9816" data-mp="1jb64rwfcoeiq">
                        Messages
                      </div>
                    </div>
                    <div data-id="element-9817" data-mp="19kjsoc6ue64s">
                      <div className="text-2xl font-black text-white" data-id="element-9818" data-mp="19js9y1ycg2vx">
                        {patterns.length}
                      </div>
                      <div className="text-[10px] text-white/40 font-mono uppercase tracking-widest" data-id="element-9819" data-mp="14k5v5o2vael0">
                        Patterns
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-9820" data-mp="15n32rgc0pp8d">
                    <Activity className={`w-4 h-4 ${theme.text}`} data-id="element-9821" data-mp="15ljmhpqd3e6l" />
                    <span className="text-sm font-black text-white tracking-tight" data-id="element-9822" data-mp="1ae2um76a5h40">
                      ToxicDetector.ai
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3 mb-12" data-id="element-9823" data-mp="1odkqf6hztumv">
              <button onClick={handleDownloadImage} disabled={downloading} className="w-full bg-white text-black text-lg font-black py-4 rounded-2xl hover:bg-gray-200 transition-all shadow-lg flex items-center justify-center gap-3 disabled:opacity-50" data-id="element-9824" data-mp="1oevtt57j10dg">
                {downloading ? <>
                    <RefreshCw className="animate-spin" data-id="element-9825" data-mp="3x7o9h3m0p7l" /> Generating...
                  </> : <>
                    <Download size={20} data-id="element-9826" data-mp="8yeuvcu0nico" /> Save Image
                  </>}
              </button>

              <button onClick={() => setShowShareMenu(!showShareMenu)} className="w-full bg-white/10 text-white text-lg font-black py-4 rounded-2xl hover:bg-white/20 transition-all border border-white/20 flex items-center justify-center gap-3" data-id="element-9827" data-mp="e09jd2f8job">
                <Share2 size={20} data-id="element-9828" data-mp="5fpi8m268l98" /> Share Verdict
              </button>

              {showShareMenu && <motion.div initial={{
            opacity: 0,
            y: -10
          }} animate={{
            opacity: 1,
            y: 0
          }} className="bg-white/5 backdrop-blur-xl rounded-2xl p-4 border border-white/10" data-id="element-9829" data-mp="3dsn2dh0azg3y">
                  <div className="grid grid-cols-2 gap-3" data-id="element-9830" data-mp="2v36q5wf99z7p">
                    <button onClick={() => handleShareToSocial('instagram')} className="bg-gradient-to-br from-purple-500 to-pink-500 text-white py-3 rounded-xl font-bold flex items-center justify-center gap-2" data-id="element-9831" data-mp="2q55555y9rptl">
                      <Instagram size={18} data-id="element-9832" data-mp="1hjrr8u5vnomq" /> Instagram
                    </button>
                    <button onClick={() => handleShareToSocial('tiktok')} className="bg-black text-white py-3 rounded-xl font-bold border border-white/20 flex items-center justify-center gap-2" data-id="element-9833" data-mp="1wc3bnma83wl2">
                      <MessageCircle size={18} data-id="element-9834" data-mp="20d5e0bzfsor9" /> TikTok
                    </button>
                    <button onClick={() => handleShareToSocial('twitter')} className="bg-blue-500 text-white py-3 rounded-xl font-bold flex items-center justify-center gap-2" data-id="element-9835" data-mp="2f9cvv5ndhe3d">
                      <Twitter size={18} data-id="element-9836" data-mp="kw59819jw8st" /> Twitter
                    </button>
                    <button onClick={() => handleShareToSocial('whatsapp')} className="bg-green-500 text-white py-3 rounded-xl font-bold flex items-center justify-center gap-2" data-id="element-9837" data-mp="fxk7tyikbj2s">
                      <MessageCircle size={18} data-id="element-9838" data-mp="2wl1m1svfvvbq" /> WhatsApp
                    </button>
                  </div>
                </motion.div>}

              <Link to="/upload" className="block w-full text-white/60 text-sm font-bold py-4 text-center hover:text-white transition-colors uppercase tracking-widest" data-id="element-9839" data-mp="2msdvvgyysplz">
                Scan Another Chat
              </Link>
            </div>
          </motion.div>}
      </div>
    </div>;
}
// Helper functions
function getPatternIcon(type: string): React.ReactNode {
  const iconMap: Record<string, React.ReactNode> = {
    Gaslighting: <AlertTriangle size={20} className="text-white" data-id="element-9840" data-mp="3t8pqx96hprdc" />,
    Manipulation: <Shield size={20} className="text-white" data-id="element-9841" data-mp="3t7enjagyilmr" />,
    Dismissiveness: <MessageSquare size={20} className="text-white" data-id="element-9842" data-mp="22enj6e0ceiu" />,
    'Blame Shifting': <TrendingDown size={20} className="text-white" data-id="element-9843" data-mp="m14h3brihqz" />,
    'Emotional Abuse': <AlertTriangle size={20} className="text-white" data-id="element-9844" data-mp="3rsc0gl1x0sbg" />
  };
  return iconMap[type] || <AlertTriangle size={20} className="text-white" data-id="element-9845" data-mp="3ruf1e04lw5uy" />;
}
function getPatternColorClass(severity: string): string {
  return severity === 'HIGH' ? 'text-red-400 border-red-400/30 bg-red-400/10' : severity === 'MEDIUM' ? 'text-amber-400 border-amber-400/30 bg-amber-400/10' : 'text-emerald-400 border-emerald-400/30 bg-emerald-400/10';
}